import { supabase } from '@/integrations/supabase/client';

/**
 * Transform a Supabase Storage public URL to use the on-the-fly image
 * transformation endpoint (resizes + serves WebP via content negotiation).
 * Falls back to the original URL for non-Supabase URLs.
 */
export function optimizedImageUrl(
  url: string | null | undefined,
  opts: { width?: number; quality?: number } = {}
): string {
  if (!url) return '';
  if (!url.includes('/storage/v1/object/public/')) return url;
  const { width = 600, quality = 70 } = opts;
  const [base, query = ''] = url.split('?');
  const transformed = base.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const params = new URLSearchParams(query);
  params.set('width', String(width));
  params.set('quality', String(quality));
  return `${transformed}?${params.toString()}`;
}


export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/avatar.${fileExt}`;

  // Delete existing avatar first (if any)
  await supabase.storage.from('avatars').remove([fileName]);

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(fileName, file, { upsert: true });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage
    .from('avatars')
    .getPublicUrl(fileName);

  return `${data.publicUrl}?t=${Date.now()}`;
}

export async function uploadShowcaseImage(userId: string, file: File): Promise<string> {
  const fileExt = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = `${userId}/showcase/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(fileName, file, { upsert: false, contentType: file.type });
  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from('avatars').getPublicUrl(fileName);
  return data.publicUrl;
}

export async function deleteShowcaseImage(url: string): Promise<void> {
  try {
    const marker = '/storage/v1/object/public/avatars/';
    const idx = url.indexOf(marker);
    if (idx === -1) return;
    const path = url.slice(idx + marker.length).split('?')[0];
    await supabase.storage.from('avatars').remove([path]);
  } catch {
    // ignore
  }
}

export async function uploadActivityImage(
  instructorId: string,
  activityId: string,
  file: File,
  index: number
): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${instructorId}/${activityId}/${index}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('activity-images')
    .upload(fileName, file, { upsert: true });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage
    .from('activity-images')
    .getPublicUrl(fileName);

  return `${data.publicUrl}?t=${Date.now()}`;
}

export async function uploadActivityImages(
  instructorId: string,
  activityId: string,
  files: File[]
): Promise<string[]> {
  const urls: string[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const url = await uploadActivityImage(instructorId, activityId, files[i], i);
    urls.push(url);
  }
  
  return urls;
}

export async function deleteActivityImages(
  instructorId: string,
  activityId: string
): Promise<void> {
  const { data: files } = await supabase.storage
    .from('activity-images')
    .list(`${instructorId}/${activityId}`);

  if (files && files.length > 0) {
    const filePaths = files.map(f => `${instructorId}/${activityId}/${f.name}`);
    await supabase.storage.from('activity-images').remove(filePaths);
  }
}

export async function uploadActivityVideo(
  instructorId: string,
  activityId: string,
  file: File
): Promise<string> {
  const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
  const fileName = `${instructorId}/${activityId}/video-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('activity-videos')
    .upload(fileName, file, { upsert: true, contentType: file.type || 'video/mp4' });
  if (error) throw error;
  const { data } = supabase.storage.from('activity-videos').getPublicUrl(fileName);
  return data.publicUrl;
}
